// @ts-strict-ignore
import "reflect-metadata/lite";
import { container, inject, injectWithTransform, singleton } from "tsyringe";
import Cookies from "js-cookie";
import {
  GeoCookie,
  Logger,
  LoggerTransformer,
  randomUuid,
  reportError,
} from "@identity/utils";
import {
  Action,
  CommunicationService,
  eventsRepository,
  GooglePrivacySandbox,
  HemTrackingService,
  InstantConfigService,
  MetricReporterService,
  OptInService,
  PartnersService,
  PpidProvider,
  TargetingService,
  TrackingContextSetupService,
} from "@identity/services";
import { COOKIES, NAVIGATOR, UUID, WINDOW } from "./symbols";
import { parseVersion } from "./utils/parse-version";
import { installConsentCallbackQueue } from "./services/ConsentQueue/consentCallbackQueue";

container.register(COOKIES, { useValue: Cookies });
container.register(UUID, { useValue: randomUuid });
container.register(NAVIGATOR, { useValue: navigator });
container.register(WINDOW, { useValue: window });

@singleton()
class IdentityEngine {
  constructor(
    @inject(GeoCookie) private readonly geoCookie: GeoCookie,
    @inject(CommunicationService)
    private readonly communicationService: CommunicationService,
    @inject(OptInService)
    private readonly optInService: OptInService,
    @inject(HemTrackingService)
    private readonly hemTrackingService: HemTrackingService,
    @inject(TrackingContextSetupService)
    private readonly trackingContextSetupService: TrackingContextSetupService,
    @inject(InstantConfigService)
    private readonly instantConfigService: InstantConfigService,
    @inject(WINDOW)
    private readonly window: Window,
    @injectWithTransform(Logger, LoggerTransformer, "IdentityEngine")
    private readonly logger: Logger,
    @inject(PpidProvider) private readonly ppidProvider: PpidProvider,
    @inject(MetricReporterService)
    private readonly metricReporterService: MetricReporterService,
    @inject(PartnersService)
    private readonly partnersService: PartnersService,
    @inject(GooglePrivacySandbox)
    private readonly googlePrivacySandbox: GooglePrivacySandbox,
    @inject(TargetingService)
    private readonly targetingService: TargetingService,
  ) {}

  public async init(): Promise<void> {
    await this.geoCookie.setGeoCookieIfNotExist();
    await this.instantConfigService.init(this.communicationService);
    this.optInService.init(this.instantConfigService);
    this.metricReporterService.init(this.instantConfigService);
    this.metricReporterService.reportIdentityEngineState("init");
  }

  public run(): void {
    this.logger.info(
      "Starting identity engine",
      `version: ${parseVersion(this.window.ads?.version?.identity)}`,
    );

    this.optInService.onReady().then(() => {
      this.trackingContextSetupService.setup();

      const { ready, blocking, optOut } = this.window.fandomCmp;
      const consent = ready && !blocking && !optOut;

      this.metricReporterService.reportConsentEvent(consent);
      this.updateGTMConsent(consent);
      this.initializeTargeting();

      if (consent) {
        this.initializeTracking();
      } else {
        this.loadPhoenixThirdPartyDisabledMode();
        this.logger.info("Tracking rejected");
      }

      this.communicationService.emit(
        new Action(eventsRepository.IDENTITY_READY),
      );
      this.metricReporterService.reportIdentityEngineState("ready");
    });
  }

  private updateGTMConsent(granted: boolean): void {
    if (this.window.dataLayer === undefined) {
      this.window.dataLayer = [];
    }

    this.window.dataLayer.push({
      event: "user-consent",
      consent: granted.toString(),
    });
  }

  private async initializeTracking() {
    this.logger.info("Setting up tracking");
    this.hemTrackingService.activateTracking();
    await this.ppidProvider.setPpid();
    await this.partnersService.initializeTrackingPartners();
    this.partnersService.createPartnersContext();
    this.googlePrivacySandbox.init();
  }

  private async initializeTargeting() {
    await this.targetingService.initializeTargeting();
  }

  // TODO: remove the method and call to it after COTECH-721 is closed
  private loadPhoenixThirdPartyDisabledMode() {
    if (!window?.PhoenixLoader?.triggerEvent) {
      this.logger.info("No PhoenixLoader.triggerEvent defined");
      return;
    }

    this.logger.info("Load third party in disabled mode");
    window.PhoenixLoader.triggerEvent("loadThirdPartyDisabled");
  }
}

async function initialize() {
  if (window.fandomCmp === undefined) {
    window.fandomCmp = {
      ready: false,
      blocking: false,
      coppa: false,
      optOut: false,
      allowed: {
        ads: false,
        tracking: false,
      },
    };
  }

  installConsentCallbackQueue();

  try {
    const identityEngine = container.resolve(IdentityEngine);
    await identityEngine.init();
    identityEngine.run();
  } catch (e) {
    void reportError(e);
  }
}

initialize();
